// Presentation-authored formatting only. Bound observation/catalog strings are
// inserted as text nodes after parsing and can never introduce markup.
export const MAX_RICH_TEXT = 8192;
export const MAX_RICH_NODES = 512;
export const MAX_RICH_DEPTH = 8;
export const MAX_RICH_ICONS = 128;
export const MAX_RESOLVED_RICH_TEXT = 8192;

export interface RichStyle {
  bold?: boolean;
  italic?: boolean;
  font?: 'display' | 'body' | 'mono';
  color?: string;
}

export type RichNode =
  | { type: 'text'; text: string; style: RichStyle }
  | { type: 'value'; binding: string; style: RichStyle }
  | { type: 'icon'; key: string; style: RichStyle }
  | { type: 'break' };

const KEY = /^[A-Za-z0-9][A-Za-z0-9:_.\-/]{0,127}$/;
const COLOR = /^#[0-9a-fA-F]{6}$/;

/** A finite grammar: b/i/font/color, icon/value, br and escaped [, ] or \\. */
export function parseRichText(template: string): RichNode[] {
  if (template.length > MAX_RICH_TEXT) throw new Error('Rich text exceeds 8192 characters.');
  const nodes: RichNode[] = [];
  const stack: { name: string; style: RichStyle }[] = [];
  let style: RichStyle = {}, text = '', icons = 0;
  const add = (node: RichNode) => {
    if (nodes.length >= MAX_RICH_NODES) throw new Error('Rich text exceeds 512 nodes.');
    nodes.push(node);
  };
  const flush = () => {
    if (text) { add({ type: 'text', text, style: { ...style } }); text = ''; }
  };
  for (let i = 0; i < template.length;) {
    const character = template[i]!;
    if (character === '\\') {
      const next = template[i + 1];
      if (next !== '[' && next !== ']' && next !== '\\') throw new Error('Escape only brackets and backslash.');
      text += next; i += 2; continue;
    }
    if (character === '\n' || character === '\r') {
      flush(); add({ type: 'break' });
      i += character === '\r' && template[i + 1] === '\n' ? 2 : 1;
      continue;
    }
    if (character !== '[') { text += character; i++; continue; }
    const end = template.indexOf(']', i + 1);
    if (end < 0) throw new Error('Unclosed rich-text tag.');
    const tag = template.slice(i + 1, end);
    flush(); i = end + 1;
    if (tag.startsWith('/')) {
      const previous = stack.pop();
      if (!previous || previous.name !== tag.slice(1)) throw new Error('Rich-text tags must close in nesting order.');
      style = previous.style; continue;
    }
    if (tag === 'br') { add({ type: 'break' }); continue; }
    if (tag.startsWith('icon=')) {
      if (++icons > MAX_RICH_ICONS) throw new Error('Rich text exceeds 128 inline icons.');
      const key = tag.slice(5);
      if (!KEY.test(key)) throw new Error('Use a declared rich-text icon key.');
      add({ type: 'icon', key, style: { ...style } }); continue;
    }
    if (tag.startsWith('value=')) {
      const binding = tag.slice(6);
      if (!binding || binding.length > 256) throw new Error('Use a bounded rich-text value binding.');
      add({ type: 'value', binding, style: { ...style } }); continue;
    }
    const name = tag.split('=')[0]!;
    const next = { ...style };
    if (tag === 'b') next.bold = true;
    else if (tag === 'i') next.italic = true;
    else if (name === 'font' && /^(display|body|mono)$/.test(tag.slice(5))) next.font = tag.slice(5) as RichStyle['font'];
    else if (name === 'color' && COLOR.test(tag.slice(6))) next.color = tag.slice(6);
    else throw new Error(`Unsupported rich-text tag: ${tag}.`);
    if (stack.length >= MAX_RICH_DEPTH) throw new Error('Rich text exceeds nesting depth 8.');
    stack.push({ name, style }); style = next;
  }
  flush();
  if (stack.length) throw new Error('Unclosed rich-text style.');
  return nodes;
}

/** Resolution never reparses bound values, even if they contain valid tags. */
export function resolveRichValues(nodes: RichNode[], value: (binding: string) => string | null): RichNode[] {
  let length = 0;
  return nodes.map(node => {
    const resolved: RichNode = node.type === 'value'
    ? { type: 'text', text: value(node.binding) ?? '', style: node.style }
    : node;
    length += resolved.type === 'text' ? resolved.text.length : 0;
    if (length > MAX_RESOLVED_RICH_TEXT) throw new Error('Resolved rich text exceeds 8192 characters.');
    return resolved;
  });
}
